import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Descriptions, Form, Input, Modal, Row, Select, Space, Statistic, Table, Tabs, Tag, message } from 'antd';
import { CheckOutlined, EyeOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { banUser, getUserDetail, getUsers, unbanUser } from '../../api/users';
import UserUsage from './UserUsage';
import { userStatusPresentation } from './userStatus';
import { channelLabel, distributionChannelOptions } from '../../content/distributionChannels';

const { TextArea } = Input;

const membershipColorMap = {
  FREE: 'default',
  MONTHLY: 'blue',
  YEARLY: 'gold',
};

const membershipLabelMap = {
  FREE: '普通',
  MONTHLY: '月卡',
  YEARLY: '年卡',
};

const formatAmount = (value) => `¥${Number(value || 0).toFixed(2)}`;

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPhone = searchParams.get('phone') || '';
  const initialUserId = searchParams.get('userId') || '';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const initialChannel = searchParams.get('registrationChannel') || '';
  const [filters, setFilters] = useState({ phone: initialPhone, userId: initialUserId, registrationChannel: initialChannel });
  const [appliedFilters, setAppliedFilters] = useState({ phone: initialPhone, userId: initialUserId, registrationChannel: initialChannel });
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const detailRequest = useRef(0);

  const [banVisible, setBanVisible] = useState(false);
  const [banLoading, setBanLoading] = useState(false);
  const [banUserId, setBanUserId] = useState(null);
  const [banForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getUsers({
        page: currentPage,
        size: currentPageSize,
        phone: appliedFilters.phone || undefined,
        userId: appliedFilters.userId || undefined,
        registrationChannel: appliedFilters.registrationChannel || undefined,
      });
      setData(result.list);
      setPagination((prev) => ({ ...prev, total: result.total }));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));

    const params = {};
    if (nextFilters.phone) {
      params.phone = nextFilters.phone;
    }
    if (nextFilters.userId) {
      params.userId = nextFilters.userId;
    }
    if (nextFilters.registrationChannel) params.registrationChannel = nextFilters.registrationChannel;
    setSearchParams(params);
  };

  const handleViewDetail = async (userId) => {
    const request = ++detailRequest.current;
    setUserDetail(null);
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const result = await getUserDetail(userId);
      if (request === detailRequest.current) setUserDetail(result);
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };

  const handleBan = (userId) => {
    setBanUserId(userId);
    setBanVisible(true);
  };

  const handleBanSubmit = async () => {
    try {
      const values = await banForm.validateFields();
      setBanLoading(true);
      await banUser(banUserId, values);
      message.success('封禁成功');
      setBanVisible(false);
      banForm.resetFields();
      fetchData();
    } catch (error) {
      console.error('Failed to ban user:', error);
    } finally {
      setBanLoading(false);
    }
  };

  const handleUnban = async (userId) => {
    Modal.confirm({
      title: '确认解封',
      content: '确定要解封该用户吗？',
      onOk: async () => {
        await unbanUser(userId);
        message.success('解封成功');
        fetchData();
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '手机号', dataIndex: 'phone' },
    { title: '昵称', dataIndex: 'nickname' },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => (
        <Tag color={userStatusPresentation(status).color}>
          {userStatusPresentation(status).label}
        </Tag>
      ),
    },
    {
      title: '会员类型',
      dataIndex: 'membershipType',
      render: (type) => (
        <Tag color={membershipColorMap[type]}>
          {membershipLabelMap[type] || type}
        </Tag>
      ),
    },
    {
      title: '钱包余额',
      dataIndex: 'walletBalance',
      render: formatAmount,
    },
    {
      title: '注册渠道',
      dataIndex: 'registrationChannel',
      render: (channel) => <Tag>{channelLabel(channel)}</Tag>,
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record.id)}>
            详情
          </Button>
          {record.status === 1 ? (
            <Button size="small" danger icon={<StopOutlined />} onClick={() => handleBan(record.id)}>
              封禁
            </Button>
          ) : record.status === 0 ? (
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleUnban(record.id)}>
              解封
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2>用户管理</h2>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="搜索手机号"
          value={filters.phone}
          onChange={(e) => setFilters((prev) => ({ ...prev, phone: e.target.value }))}
          onPressEnter={handleSearch}
          style={{ width: 200 }}
        />
        <Select
          allowClear
          placeholder="注册渠道"
          value={filters.registrationChannel || undefined}
          options={distributionChannelOptions}
          onChange={(value) => setFilters((prev) => ({ ...prev, registrationChannel: value || '' }))}
          style={{ width: 170 }}
        />
        <Input
          placeholder="用户ID"
          value={filters.userId}
          onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
          onPressEnter={handleSearch}
          style={{ width: 140 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          搜索
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total }),
        }}
      />

      <Modal
        title="用户详情"
        open={detailVisible}
        onCancel={() => { detailRequest.current += 1; setDetailVisible(false); setUserDetail(null); }}
        destroyOnHidden
        footer={null}
        width={860}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div>
        ) : userDetail && detailVisible ? (
          <Tabs key={userDetail.id} items={[{ key: 'profile', label: '基本资料', children: (
          <div>
            <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="ID">{userDetail.id}</Descriptions.Item>
                <Descriptions.Item label="手机号">{userDetail.phone}</Descriptions.Item>
                <Descriptions.Item label="昵称">{userDetail.nickname || '-'}</Descriptions.Item>
                <Descriptions.Item label="实名">{userDetail.realName || '-'}</Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={userStatusPresentation(userDetail.status).color}>
                    {userStatusPresentation(userDetail.status).label}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="邀请码">{userDetail.inviteCode || '-'}</Descriptions.Item>
                <Descriptions.Item label="邀请人ID">{userDetail.invitedByUserId || '-'}</Descriptions.Item>
                <Descriptions.Item label="注册渠道">{channelLabel(userDetail.registrationChannel)}</Descriptions.Item>
                <Descriptions.Item label="最后登录 IP">{userDetail.lastLoginIp || '-'}</Descriptions.Item>
                <Descriptions.Item label="注册时间">
                  {userDetail.createdAt ? dayjs(userDetail.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="最后登录">
                  {userDetail.lastLoginTime ? dayjs(userDetail.lastLoginTime).format('YYYY-MM-DD HH:mm') : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card size="small" title="邀请摘要">
                  <Statistic title="已邀请" value={userDetail.inviteSummary?.invitedCount || 0} />
                  <div style={{ marginTop: 12, color: '#666' }}>
                    达标 {userDetail.inviteSummary?.qualifiedCount || 0} 人，奖励发放 {userDetail.inviteSummary?.rewardGrantedCount || 0} 次
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="返利摘要">
                  <Statistic title="返利记录" value={userDetail.rebateSummary?.recordCount || 0} />
                  <div style={{ marginTop: 12, color: '#666' }}>
                    待结算 {formatAmount(userDetail.rebateSummary?.pendingAmount)}，已结算 {formatAmount(userDetail.rebateSummary?.settledAmount)}
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="提现摘要">
                  <Statistic title="提现申请" value={userDetail.withdrawalSummary?.requestCount || 0} />
                  <div style={{ marginTop: 12, color: '#666' }}>
                    待审核 {userDetail.withdrawalSummary?.pendingCount || 0} 笔，累计 {formatAmount(userDetail.withdrawalSummary?.totalAmount)}
                  </div>
                </Card>
              </Col>
            </Row>

            <Card title="联动入口" size="small" style={{ marginBottom: 16 }}>
              <Space wrap>
                <Button onClick={() => navigate(`/rebates?userId=${userDetail.id}`)}>查看返利记录</Button>
                <Button onClick={() => navigate(`/withdrawals?userId=${userDetail.id}`)}>查看提现记录</Button>
                <Button onClick={() => navigate(`/invites?inviterUserId=${userDetail.id}`)}>查看邀请关系</Button>
              </Space>
            </Card>

            {userDetail.membership && (
              <Card title="会员信息" size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={2}>
                  <Descriptions.Item label="类型">
                    <Tag color={membershipColorMap[userDetail.membership.type]}>
                      {membershipLabelMap[userDetail.membership.type] || userDetail.membership.type}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="返利比例">
                    {Number(userDetail.membership.rebateRate || 0) * 100}%
                  </Descriptions.Item>
                  <Descriptions.Item label="开始时间">
                    {userDetail.membership.startTime ? dayjs(userDetail.membership.startTime).format('YYYY-MM-DD') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="到期时间">
                    {userDetail.membership.expireTime ? dayjs(userDetail.membership.expireTime).format('YYYY-MM-DD') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {userDetail.wallet && (
              <Card title="钱包信息" size="small">
                <Descriptions column={2}>
                  <Descriptions.Item label="可用余额">{formatAmount(userDetail.wallet.availableBalance)}</Descriptions.Item>
                  <Descriptions.Item label="锁定余额">{formatAmount(userDetail.wallet.lockedBalance)}</Descriptions.Item>
                  <Descriptions.Item label="待结算">{formatAmount(userDetail.wallet.pendingBalance)}</Descriptions.Item>
                  <Descriptions.Item label="累计收益">{formatAmount(userDetail.wallet.totalEarned)}</Descriptions.Item>
                  <Descriptions.Item label="累计提现">{formatAmount(userDetail.wallet.totalWithdrawn)}</Descriptions.Item>
                  <Descriptions.Item label="负债余额">{formatAmount(userDetail.wallet.debtBalance)}</Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {userDetail.isBlacklisted && (
              <Card title="黑名单信息" size="small" style={{ marginTop: 16 }}>
                <Tag color="red">已封禁</Tag>
                <span style={{ marginLeft: 8 }}>原因: {userDetail.blacklistReason}</span>
              </Card>
            )}
          </div>
          ) }, { key: 'usage', label: '使用情况', children: <UserUsage key={userDetail.id} userId={userDetail.id} /> }]} />
        ) : null}
      </Modal>

      <Modal
        title="封禁用户"
        open={banVisible}
        onCancel={() => setBanVisible(false)}
        onOk={handleBanSubmit}
        confirmLoading={banLoading}
      >
        <Form form={banForm} layout="vertical">
          <Form.Item
            name="reason"
            label="封禁原因"
            rules={[{ required: true, message: '请输入封禁原因' }]}
          >
            <TextArea rows={3} placeholder="请输入封禁原因" />
          </Form.Item>
          <Form.Item name="reasonCode" label="原因代码">
            <Input placeholder="可选，如: FRAUD, ABUSE" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
